import { useState, useEffect } from 'react';
import { requestNotificationPermission, onMessageListener } from '../lib/firebase/config';
import { 
  sendFCMTokenToBackend, 
  removeFCMTokenFromBackend, 
  storeFCMToken, 
  getStoredFCMToken, 
  removeStoredFCMToken 
} from '../utils/fcm-token-manager';

interface NotificationPermission {
  granted: boolean;
  token: string | null;
  supported: boolean;
}

export function useFirebaseMessaging() {
  const [permission, setPermission] = useState<NotificationPermission>({
    granted: false,
    token: null,
    supported: false
  });
  const [notification, setNotification] = useState<any>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized) {
      return;
    }

    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      setPermission(prev => ({ ...prev, supported: false }));
      setHasInitialized(true);
      return;
    }

    // Check if Firebase SDK is supported
    const checkFirebaseSupport = async () => {
      try {
        // Test if Firebase functions are available
        const testToken = await requestNotificationPermission();
        if (testToken !== null) {
          setPermission(prev => ({ ...prev, supported: true }));
        } else {
          console.warn('Firebase messaging not supported in this browser');
          setPermission(prev => ({ ...prev, supported: false }));
        }
      } catch (error) {
        console.warn('Firebase SDK not supported:', error);
        setPermission(prev => ({ ...prev, supported: false }));
        return;
      }
    };

    // Check current permission status
    const checkPermission = async () => {
      try {
        if (Notification.permission === 'granted') {
          const token = await requestNotificationPermission();
          if (token) {
            setPermission({
              granted: true,
              token,
              supported: true
            });
            // Store token locally but don't auto-register
            storeFCMToken(token);
          }
        } else if (Notification.permission === 'denied') {
          setPermission({
            granted: false,
            token: null,
            supported: true
          });
        }
      } catch (error) {
        console.error('Error checking notification permission:', error);
        setPermission(prev => ({ ...prev, supported: false }));
      }
    };

    // Check Firebase support first, then check permissions
    checkFirebaseSupport().then(() => {
      if (permission.supported) {
        checkPermission();
      }
      setHasInitialized(true);
    });

    // Listen for foreground messages only if Firebase is supported
    if (permission.supported) {
      onMessageListener()
        .then((payload) => {
          setNotification(payload);
        })
        .catch((err) => {
          console.error('Error receiving foreground message:', err);
        });
    }
  }, [hasInitialized]); // Add hasInitialized as dependency

  const requestPermission = async (): Promise<boolean> => {
    try {
      // Check if Firebase is supported first
      if (!permission.supported) {
        console.warn('Firebase messaging not supported in this browser');
        return false;
      }

      const token = await requestNotificationPermission();
      if (token) {
        setPermission({
          granted: true,
          token,
          supported: true
        });
        // Store token locally but don't auto-register
        storeFCMToken(token);
        return true;
      } else {
        setPermission({
          granted: false,
          token: null,
          supported: true
        });
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setPermission(prev => ({ ...prev, supported: false }));
      return false;
    }
  };

  const clearNotification = () => {
    setNotification(null);
  };

  const registerFCMToken = async (): Promise<boolean> => {
    try {
      const currentToken = getStoredFCMToken();
      if (!currentToken) {
        console.error('No FCM token available for registration');
        return false;
      }

      if (isRegistering) {
        console.log('FCM registration already in progress');
        return false;
      }

      setIsRegistering(true);
      try {
        const success = await sendFCMTokenToBackend(currentToken);
        if (success) {
          console.log('FCM token registered successfully');
        } else {
          console.error('Failed to register FCM token');
        }
        return success;
      } finally {
        setIsRegistering(false);
      }
    } catch (error) {
      console.error('Error registering FCM token:', error);
      setIsRegistering(false);
      return false;
    }
  };

  const revokePermission = async (): Promise<boolean> => {
    try {
      const currentToken = getStoredFCMToken();
      if (currentToken) {
        await removeFCMTokenFromBackend(currentToken);
        removeStoredFCMToken();
      }
      
      setPermission({
        granted: false,
        token: null,
        supported: permission.supported
      });
      
      return true;
    } catch (error) {
      console.error('Error revoking notification permission:', error);
      return false;
    }
  };

  return {
    permission,
    notification,
    requestPermission,
    registerFCMToken,
    clearNotification,
    revokePermission,
    isRegistering
  };
}
